import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useCreateAnnouncement } from '../../hooks/useAnnouncements';
import { Announcement, PulseType, PulseSeverity } from '../../types';
import { useNodeRoles, useDescendantCount } from '../../hooks/useNode';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { AnnouncementCard } from './AnnouncementCard';
import { Check, Info, AlertTriangle, Layers, Globe, Image as ImageIcon, Link as LinkIcon, Smile, Video, Settings, Users, MessageSquare, Bell, Newspaper, Calendar, Shield, CreditCard } from 'lucide-react';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import toast from 'react-hot-toast';

const schema = z.object({
  title: z.string().min(1, 'Title is required').max(500, 'Title is too long'),
  body: z.string().min(1, 'Body is required'),
  type: z.nativeEnum(PulseType),
  severity: z.nativeEnum(PulseSeverity),
  audience: z.enum(['MEMBERS', 'PUBLIC']),
  targetRoles: z.array(z.string()).min(1, 'Select at least one role'),
  cascadesDown: z.boolean(),
  requiresAck: z.boolean(),
  postToFacebook: z.boolean().optional(),
  expiresAt: z.string().optional(),
  linkUrl: z.string().optional().or(z.literal('')),
  linkText: z.string().optional().or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

interface AnnouncementComposerProps {
  nodeId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const AnnouncementComposer: React.FC<AnnouncementComposerProps> = ({
  nodeId,
  isOpen,
  onClose,
}) => {
  const { data: roles = [] } = useNodeRoles(nodeId);
  const { data: descendantCount } = useDescendantCount(nodeId);
  const createMutation = useCreateAnnouncement(nodeId);
  
  const [showConfirmCascade, setShowConfirmCascade] = useState(false);
  const [pendingData, setPendingData] = useState<FormData | null>(null);

  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      body: '',
      type: PulseType.GENERAL,
      severity: PulseSeverity.MEDIUM,
      audience: 'MEMBERS',
      targetRoles: ['All roles'],
      cascadesDown: false,
      requiresAck: false,
      postToFacebook: false,
      linkUrl: '',
      linkText: '',
    },
  });

  const formData = watch();

  const handleRoleToggle = (role: string) => {
    const current = formData.targetRoles;
    if (role === 'All roles') {
      setValue('targetRoles', ['All roles']);
    } else {
      let next = current.filter(r => r !== 'All roles');
      if (next.includes(role)) {
        next = next.filter(r => r !== role);
        if (next.length === 0) next = ['All roles'];
      } else {
        next.push(role);
      }
      setValue('targetRoles', next);
    }
  };

  const onSubmit = (data: FormData) => {
    if (data.cascadesDown && descendantCount && descendantCount.nodeCount > 0) {
      setPendingData(data);
      setShowConfirmCascade(true);
    } else {
      executeSubmit(data);
    }
  };

  const executeSubmit = (data: FormData) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        reset();
        onClose();
      },
    });
  };

  const estimatedReach = descendantCount ? (formData.cascadesDown ? descendantCount.memberCount : 100) : 0;
  const isOverLimit = estimatedReach > 5000; // Mock limit check

  const severities: { id: PulseSeverity; label: string; icon: any; color: string; activeColor: string }[] = [
    { id: PulseSeverity.CRITICAL, label: 'Critical', icon: AlertTriangle, color: 'bg-red-50 text-red-600 border-red-100', activeColor: 'bg-red-600 text-white border-red-600' },
    { id: PulseSeverity.HIGH, label: 'High', icon: Bell, color: 'bg-amber-50 text-amber-600 border-amber-100', activeColor: 'bg-amber-600 text-white border-amber-600' },
    { id: PulseSeverity.MEDIUM, label: 'Medium', icon: MessageSquare, color: 'bg-blue-50 text-blue-600 border-blue-100', activeColor: 'bg-blue-600 text-white border-blue-600' },
    { id: PulseSeverity.LOW, label: 'Low', icon: Info, color: 'bg-gray-50 text-gray-600 border-gray-100 dark:bg-slate-950', activeColor: 'bg-gray-800 text-white border-gray-800' },
  ];

  const types: { id: PulseType; label: string; icon: any }[] = [
    { id: PulseType.GENERAL, label: 'General', icon: MessageSquare },
    { id: PulseType.SYSTEM, label: 'System', icon: Settings },
    { id: PulseType.SECURITY, label: 'Security', icon: Shield },
    { id: PulseType.GOVERNANCE, label: 'Governance', icon: Layers },
    { id: PulseType.FINANCIAL, label: 'Financial', icon: CreditCard },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="New Announcement"
        className="max-w-xl"
        footer={
          <div className="flex justify-between items-center w-full">
            <button 
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-xs font-bold text-gray-500 hover:text-brand-primary flex items-center gap-1.5"
            >
              <Settings size={14} className={cn(showAdvanced && "rotate-90 transition-transform")} />
              {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
            </button>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button 
                variant="primary" 
                onClick={handleSubmit(onSubmit)}
                isLoading={createMutation.isPending}
                className="px-8"
              >
                Post Now
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          <Input
            label="What is this about?"
            placeholder="Give your update a clear title..."
            {...register('title')}
            errorMessage={errors.title?.message}
            className="text-lg font-bold"
          />

          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setValue('type', t.id)}
                className={cn(
                  "px-3 py-1.5 rounded-full border text-xs font-bold flex items-center gap-1.5 transition-all",
                  formData.type === t.id 
                    ? "bg-brand-primary text-white border-brand-primary" 
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 dark:bg-slate-900"
                )}
              >
                <t.icon size={12} />
                {t.label}
              </button>
            ))}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Your Message</label>
            </div>
            <div className="relative group">
              <textarea
                className={cn(
                  "w-full px-4 py-3 text-sm border border-gray-200 rounded-2xl bg-gray-50 text-gray-900 focus:outline-none focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/5 min-h-[160px] transition-all dark:bg-slate-950 dark:text-white",
                  errors.body && "border-red-500 focus:border-red-500 focus:ring-red-100"
                )}
                placeholder="Share your news with the community here..."
                {...register('body')}
              />
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5 opacity-0 group-focus-within:opacity-100 transition-opacity">
                <button type="button" onClick={() => setValue('body', formData.body + '![image](https://)')} title="Add Image" className="p-2 hover:bg-white rounded-lg text-gray-400 hover:text-brand-primary transition-colors shadow-sm dark:bg-slate-900"><ImageIcon size={16} /></button>
                <button type="button" onClick={() => setValue('body', formData.body + 'https://www.youtube.com/watch?v=')} title="Add Video" className="p-2 hover:bg-white rounded-lg text-gray-400 hover:text-brand-primary transition-colors shadow-sm dark:bg-slate-900"><Video size={16} /></button>
                <button type="button" onClick={() => setValue('body', formData.body + '[title](https://)')} title="Add Link" className="p-2 hover:bg-white rounded-lg text-gray-400 hover:text-brand-primary transition-colors shadow-sm dark:bg-slate-900"><LinkIcon size={16} /></button>
              </div>
            </div>
            {errors.body && <p className="text-xs text-red-600 mt-1">{errors.body.message}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Who should see this?</label>
              <div className="flex p-1 bg-gray-100 rounded-xl dark:bg-slate-800">
                <button
                  type="button"
                  onClick={() => setValue('audience', 'MEMBERS')}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all",
                    formData.audience === 'MEMBERS' 
                      ? "bg-white text-navy-900 shadow-sm dark:bg-slate-900" 
                      : "text-gray-500 hover:text-gray-700"
                  )}
                >
                  <Users size={14} />
                  Members
                </button>
                <button
                  type="button"
                  onClick={() => setValue('audience', 'PUBLIC')}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all",
                    formData.audience === 'PUBLIC' 
                      ? "bg-white text-blue-600 shadow-sm dark:bg-slate-900" 
                      : "text-gray-500 hover:text-gray-700"
                  )}
                >
                  <Globe size={14} />
                  Public
                </button>
              </div>
              
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Est. Reach</span>
                <span className={cn(
                  "text-[10px] font-bold uppercase",
                  isOverLimit ? "text-red-500" : "text-brand-primary"
                )}>
                  {(estimatedReach || 0).toLocaleString()} members {isOverLimit && "(Over Tier Limit)"}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Severity Level</label>
              <div className="flex flex-wrap gap-2">
                {severities.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setValue('severity', s.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-lg border text-xs font-bold flex items-center gap-1.5 transition-all",
                      formData.severity === s.id 
                        ? s.activeColor 
                        : cn(s.color, "hover:scale-105")
                    )}
                  >
                    <s.icon size={12} />
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <AnimatePresence>
            {showAdvanced && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden space-y-6 pt-4 border-t border-gray-100"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Target specific roles</label>
                  <div className="flex flex-wrap gap-2">
                    {['All roles', ...roles].map((role) => {
                      const roleName = typeof role === 'string' ? role : role.name;
                      const roleId = typeof role === 'string' ? role : (role.id || role.name);
                      return (
                        <div
                          key={roleId}
                          onClick={() => handleRoleToggle(roleName)}
                          className={cn(
                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer",
                            formData.targetRoles.includes(roleName)
                              ? "bg-navy-900 text-white border-navy-900"
                              : "bg-white text-gray-700 border-gray-200 hover:border-gray-300 dark:bg-slate-900"
                          )}
                        >
                          <div className={cn("w-1.5 h-1.5 rounded-full bg-current opacity-70")} />
                          {roleName}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-brand-primary/20 transition-colors cursor-pointer group dark:bg-slate-950" onClick={() => setValue('cascadesDown', !formData.cascadesDown)}>
                      <input
                        type="checkbox"
                        checked={formData.cascadesDown}
                        onChange={() => {}}
                        className="mt-1 rounded border-gray-300 text-brand-primary focus:ring-brand-primary transition-all"
                      />
                      <div>
                        <span className="block text-sm font-bold text-navy-900">Push to all chapters</span>
                        <span className="block text-[10px] text-gray-500 font-medium">Broadcast to sub-organizations automatically.</span>
                      </div>
                    </div>

                    <div className={cn(
                      "flex items-start gap-3 p-3 rounded-xl border transition-all",
                      (formData.severity !== 'CRITICAL' && formData.severity !== 'HIGH') ? "opacity-50 grayscale cursor-not-allowed bg-gray-50 border-gray-100 dark:bg-slate-950" : "bg-gray-50 border-gray-100 hover:border-brand-primary/20 cursor-pointer dark:bg-slate-950"
                    )} onClick={() => {
                      if (formData.severity === 'CRITICAL' || formData.severity === 'HIGH') {
                        setValue('requiresAck', !formData.requiresAck);
                      }
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.requiresAck}
                        onChange={() => {}}
                        disabled={formData.severity !== 'CRITICAL' && formData.severity !== 'HIGH'}
                        className="mt-1 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                      />
                      <div>
                        <span className="block text-sm font-bold text-navy-900">Must Acknowledge</span>
                        <span className="block text-[10px] text-gray-500 font-medium">Require users to confirm they have read this.</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Input
                      label="Post Expiry"
                      type="date"
                      {...register('expiresAt')}
                      className="h-11"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Button Name"
                        placeholder="e.g. Join"
                        {...register('linkText')}
                        className="h-11"
                      />
                      <Input
                        label="Button Link"
                        placeholder="https://..."
                        {...register('linkUrl')}
                        className="h-11"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Modal>

      {/* Cascade Confirmation Modal */}
      <Modal
        isOpen={showConfirmCascade}
        onClose={() => setShowConfirmCascade(false)}
        title="Confirm Cascade"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowConfirmCascade(false)}>Back</Button>
            <Button 
              variant="primary" 
              onClick={() => {
                if (pendingData) executeSubmit(pendingData);
                setShowConfirmCascade(false);
              }}
            >
              Confirm & Post
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
          <Layers size={24} className="text-amber-700 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-1">Large Scale Broadcast</h3>
            <p className="text-sm text-amber-800 leading-relaxed">
              This will notify <span className="font-bold">{(descendantCount?.memberCount ?? 0).toLocaleString()} members</span> across <span className="font-bold">{(descendantCount?.nodeCount ?? 0).toLocaleString()} child orgs</span>. 
              This action cannot be undone easily. Are you sure you want to continue?
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};
